import * as React from 'react';
import {
    useRecordContext,
    useDataProvider,
    useTranslate,
    useNotify,
} from 'react-admin';
import { useQuery } from '@tanstack/react-query';
import {
    Box,
    Chip,
    CircularProgress,
    IconButton,
    Tooltip,
    Typography,
} from '@mui/material';
import CachedIcon from '@mui/icons-material/Cached';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Link } from 'react-router-dom';

const GUID_REGEX =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface ValkeyKeyStatus {
    id: string;
    key: string;
    exists: boolean;
    ttl: number;
}

const formatTTL = (ttl: number): string => {
    if (ttl < 0) return '-';
    const days = Math.floor(ttl / (24 * 3600));
    const hours = Math.floor((ttl % (24 * 3600)) / 3600);
    const minutes = Math.floor((ttl % 3600) / 60);
    const seconds = ttl % 60;

    const parts: string[] = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    if (days === 0 && hours === 0) parts.push(`${seconds}s`);

    return parts.join(' ');
};

/**
 * Shows whether the teacher full data (schools, classes, pupils) is populated
 * in the Valkey cache under the key teacher_full_data_{guid}.
 *
 * Uses the O(1) GET /valkey/{id}/status admin endpoint (TTL check only, the
 * potentially large cached value is never transferred to the browser).
 * Hidden entirely on fetch errors (e.g. non-superadmins get 403).
 */
const TeacherCacheStatus = () => {
    const record = useRecordContext();
    const dataProvider = useDataProvider() as any;
    const translate = useTranslate();
    const notify = useNotify();
    const [flushing, setFlushing] = React.useState(false);

    const guid: string | undefined = record?.guid;
    const cacheKey = guid ? `teacher_full_data_${guid}` : null;
    const enabled = !!cacheKey && GUID_REGEX.test(guid);

    const { data, isPending, isError, refetch, isRefetching } = useQuery({
        queryKey: ['teacher-cache-status', cacheKey],
        queryFn: () => dataProvider.getValkeyKeyStatus(cacheKey),
        enabled,
        staleTime: 30_000,
        refetchOnMount: 'always',
        retry: false,
    });

    // Ei nayteta mitaan jos guid puuttuu tai haku epaonnistui (esim. 403).
    if (!enabled || isError) return null;

    const status: ValkeyKeyStatus | undefined = data?.data;
    const exists = !!status?.exists;

    const handleFlush = async () => {
        if (!cacheKey) return;
        if (
            !window.confirm(
                translate('resources.teacher.cache.flush_confirm', {
                    key: cacheKey,
                })
            )
        ) {
            return;
        }
        setFlushing(true);
        try {
            await dataProvider.deleteValkeyKey(cacheKey);
            notify(translate('resources.teacher.cache.flushed'), {
                type: 'success',
            });
            refetch();
        } catch (e) {
            notify(translate('resources.teacher.cache.flush_error'), {
                type: 'error',
            });
        } finally {
            setFlushing(false);
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'wrap',
            }}
        >
            {isPending || !status ? (
                <Chip
                    size="small"
                    variant="outlined"
                    label="..."
                    icon={<CircularProgress size={14} />}
                />
            ) : exists ? (
                <Tooltip
                    title={
                        <>
                            <Typography variant="caption" component="div">
                                {translate('resources.teacher.cache.key')}:{' '}
                                {status.key}
                            </Typography>
                            <Typography variant="caption" component="div">
                                {translate(
                                    'resources.teacher.cache.ttl_remaining'
                                )}
                                : {formatTTL(status.ttl)}
                            </Typography>
                            {status.ttl > 0 && (
                                <Typography variant="caption" component="div">
                                    {translate(
                                        'resources.teacher.cache.expires_at'
                                    )}
                                    :{' '}
                                    {new Date(
                                        Date.now() + status.ttl * 1000
                                    ).toLocaleString('fi-FI')}
                                </Typography>
                            )}
                        </>
                    }
                    arrow
                >
                    <Chip
                        size="small"
                        color="success"
                        icon={<CachedIcon />}
                        label={translate('resources.teacher.cache.cached')}
                    />
                </Tooltip>
            ) : (
                <Chip
                    size="small"
                    color="default"
                    variant="outlined"
                    label={translate('resources.teacher.cache.not_cached')}
                />
            )}
            {!!status && status.ttl > 0 && (
                <Typography variant="caption" color="textSecondary">
                    {translate('resources.teacher.cache.ttl_remaining')}:{' '}
                    {formatTTL(status.ttl)}
                </Typography>
            )}
            <Tooltip title={translate('resources.teacher.cache.refresh')}>
                <span>
                    <IconButton
                        size="small"
                        onClick={() => refetch()}
                        disabled={isRefetching}
                    >
                        <RefreshIcon fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>
            {exists && status && (
                <>
                    <Tooltip
                        title={translate('resources.teacher.cache.view_data')}
                    >
                        <IconButton
                            size="small"
                            component={Link}
                            to={`/valkey/${encodeURIComponent(
                                status.key
                            )}/show`}
                        >
                            <VisibilityIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={translate('resources.teacher.cache.flush')}>
                        <span>
                            <IconButton
                                size="small"
                                onClick={handleFlush}
                                disabled={flushing}
                            >
                                {flushing ? (
                                    <CircularProgress size={16} />
                                ) : (
                                    <DeleteOutlineIcon fontSize="small" />
                                )}
                            </IconButton>
                        </span>
                    </Tooltip>
                </>
            )}
        </Box>
    );
};

export default TeacherCacheStatus;
